import { useState } from 'react'
import { Notice } from '../Toast'
import { Link, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { correctKey, getKeys, getVersionQuestions } from '../api/endpoints'
import { ErrorNotice, ReasonDialog } from '../components'
import { formatDateTime } from '../lib/format'

/** Corrects the answer key: a new key version is created, scores are recalculated, and the old scores are kept. */
export default function KeyEditorPage() {
  const versionId = Number(useParams().versionId)
  const queryClient = useQueryClient()
  const sections = useQuery({ queryKey: ['version-questions', versionId], queryFn: () => getVersionQuestions(versionId) })
  const keys = useQuery({ queryKey: ['keys', versionId], queryFn: () => getKeys(versionId) })
  const [edits, setEdits] = useState<Record<number, string>>({})
  const [open, setOpen] = useState(false)
  const [done, setDone] = useState<string | null>(null)

  const original = new Map<number, number | null>()
  sections.data?.forEach((s) => s.questions.forEach((q) => q.options.forEach((o) => original.set(o.id, o.marks))))
  const changes = Object.entries(edits).filter(([id, v]) => v !== '' && Number(v) !== original.get(Number(id))).map(([id, v]) => ({ optionId: Number(id), marks: Number(v) }))

  const save = useMutation({
    mutationFn: (reason: string) => correctKey(versionId, reason, changes),
    onSuccess: async (r) => {
      setOpen(false)
      setEdits({})
      setDone(`Key ${r.keyNo} created (${r.changedOptions} option${r.changedOptions === 1 ? '' : 's'} changed). Scores are being recalculated.`)
      await queryClient.invalidateQueries({ queryKey: ['version-questions', versionId] })
      await queryClient.invalidateQueries({ queryKey: ['keys', versionId] })
    },
  })

  return (
    <div>
      <p><Link to="/content">← Content</Link></p>
      <h1>Answer key</h1>
      <p className="muted">Change the marks for an option, then save with a reason. Every attempt on this version is re-scored, reports are regenerated, and earlier scores stay on record.</p>
      {done && <Notice tone="ok">{done}</Notice>}
      <ErrorNotice error={sections.error ?? keys.error} />
      {keys.data && keys.data.length > 0 && <p className="muted">Current: key {keys.data[0].keyNo} ({keys.data[0].reason ?? 'no reason recorded'}, {formatDateTime(keys.data[0].createdAt)}). {keys.data.length} version{keys.data.length === 1 ? '' : 's'} on record.</p>}
      <div className="row" style={{ position: 'sticky', top: 0, background: 'var(--bg)', padding: '8px 0', zIndex: 2 }}>
        <button type="button" className="primary" disabled={changes.length === 0} onClick={() => setOpen(true)}>Save {changes.length} change{changes.length === 1 ? '' : 's'}</button>
        <button type="button" disabled={changes.length === 0} onClick={() => setEdits({})}>Discard changes</button>
      </div>
      {sections.data?.map((s) => (
        <section className="card" key={s.id}>
          <h2>{s.name}</h2>
          {s.questions.map((q, n) => (
            <fieldset key={q.id} style={{ border: 0, borderBottom: '1px solid var(--line)', padding: '8px 0', margin: 0 }}>
              <legend style={{ fontWeight: 600 }}>{n + 1}. {q.body} {q.tags.length > 0 && <span className="muted">[{q.tags.join(', ')}]</span>}</legend>
              <div className="row">{q.options.map((o) => (
                <label key={o.id} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span title={o.body}>{o.label}</span>
                  <input aria-label={`Marks for option ${o.label} of question ${n + 1}`} type="number" step="0.5" style={{ width: 80, borderColor: edits[o.id] !== undefined && Number(edits[o.id]) !== o.marks ? 'var(--accent)' : undefined }}
                    value={edits[o.id] ?? (o.marks ?? '')} onChange={(e) => setEdits({ ...edits, [o.id]: e.target.value })} />
                </label>
              ))}</div>
              <div className="muted" style={{ fontSize: '.85rem' }}>{q.options.map((o) => `${o.label}: ${o.body}`).join(' · ')}</div>
            </fieldset>
          ))}
        </section>
      ))}
      {open && <ReasonDialog title="Save the corrected key?" description={`${changes.length} option mark${changes.length === 1 ? '' : 's'} will change and all finished attempts on this version will be re-scored.`}
        confirmLabel="Save and re-score" busy={save.isPending} error={save.error} onCancel={() => setOpen(false)} onConfirm={(r) => save.mutate(r)} />}
    </div>
  )
}
